// client/src/pages/AdminVerification.tsx - FIXED MESSAGE VIEWING AND FILTERS WITH TEST ATTRIBUTES
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';

interface UnverifiedUser {
    id: string;
    type: 'attorney' | 'expert';
    created_at: string;
    tier?: number;
    // Attorney fields
    name?: string;
    firm_name?: string;
    license_number?: string;
    phone_number?: string;
    email?: string;
    state_of_practice?: string;
    years_experience?: number;
    firm_size?: string;
    practice_areas?: string[];
    search_keywords?: string;
    // Expert fields
    full_name?: string;
    expert_type?: string;
    npi?: string;
    job_title?: string;
    specialty?: string;
    sub_specialty?: string;
    years_practicing?: number;
    certifications?: string[];
    states_licensed?: string[];
    retainer_fee?: number;
    rate_review?: number;
    rate_deposition?: number;
    rate_trial?: number;
    cv_url?: string;
    fee_schedule_url?: string;
    additional_docs_url?: string;
    profile_picture_url?: string;
    // Nursing fields
    nursing_degree?: string;
    nursing_certifications?: string[];
    years_as_nurse?: number;
    extracted_keywords?: string;
    case_experience?: string;
    is_verified?: boolean;
    user_id?: string;
    // License & Legal fields (IMPORTANT FOR ADMIN REVIEW)
    prior_license_actions?: boolean;
    license_action_details?: string;
    current_lawsuit?: boolean;
    lawsuit_details?: string;
    // Deposition/Trial counts
    num_defense_depositions?: number;
    num_plaintiff_depositions?: number;
    num_defense_trials?: number;
    num_plaintiff_trials?: number;
    // Other
    currently_practicing?: boolean;
    published_scholarly?: boolean;
    social_url?: string;
    website_url?: string;
    selected_keywords?: string[];
    // ML Data Fields
    daubert_challenge_history?: string;
    cases_won?: number;
    cases_lost?: number;
    cases_settled?: number;
    cases_pending?: number;
    languages?: string[];
    remote_testimony?: boolean;
    avg_response_time_hours?: number;
    last_active_at?: string;
    referral_sources?: string;
}

interface Message {
    id: string;
    created_at: string;
    sender_type: 'attorney' | 'expert';
    message: string;
    case_title?: string;
    urgency?: string;
    read: boolean;
    attorney_id?: string;
    expert_id?: string;
    attorneys?: {
        name: string;
        email: string;
        firm_name: string;
    };
    experts?: {
        full_name: string;
        email: string;
        specialty: string;
    };
}

const TIER_COLORS = {
    1: {
        border: 'border-yellow-500',
        shadow: 'shadow-yellow-200',
        bg: 'bg-yellow-50',
        text: 'text-yellow-800',
        name: 'Gold Tier',
        description: 'Elite experts with exceptional credentials'
    },
    2: {
        border: 'border-purple-500',
        shadow: 'shadow-purple-200',
        bg: 'bg-purple-50',
        text: 'text-purple-800',
        name: 'Purple Tier',
        description: 'Highly experienced experts'
    },
    3: {
        border: 'border-blue-500',
        shadow: 'shadow-blue-200',
        bg: 'bg-blue-50',
        text: 'text-blue-800',
        name: 'Blue Tier',
        description: 'Professional experts (Standard)'
    },
    4: {
        border: 'border-green-500',
        shadow: 'shadow-green-200',
        bg: 'bg-green-50',
        text: 'text-green-800',
        name: 'Green Tier',
        description: 'Qualified experts'
    },
    5: {
        border: 'border-gray-500',
        shadow: 'shadow-gray-200',
        bg: 'bg-gray-50',
        text: 'text-gray-800',
        name: 'Silver Tier',
        description: 'Entry-level experts'
    }
};

const TIER_NAMES = {
    1: 'Premium Gold',
    2: 'Premium Purple',
    3: 'Professional',
    4: 'Standard',
    5: 'Basic'
};


export default function AdminVerification() {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'pending' | 'verified' | 'messages' | 'overview'>('overview');

    // Data states
    const [unverifiedUsers, setUnverifiedUsers] = useState<UnverifiedUser[]>([]);
    const [verifiedUsers, setVerifiedUsers] = useState<UnverifiedUser[]>([]);
    const [allMessages, setAllMessages] = useState<Message[]>([]);
    const [stats, setStats] = useState({
        totalUsers: 0,
        pendingVerification: 0,
        verifiedUsers: 0,
        totalMessages: 0,
        expertsCount: 0,
        attorneysCount: 0,
        nursesCount: 0
    });

    // UI states
    const [selectedUser, setSelectedUser] = useState<UnverifiedUser | null>(null);
    const [verifying, setVerifying] = useState<string | null>(null);
    const [customKeywords, setCustomKeywords] = useState('');
    const [addingKeywords, setAddingKeywords] = useState<string | null>(null);
    const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
    const [selectedTier, setSelectedTier] = useState<number>(3);
    const [editingCaseExperience, setEditingCaseExperience] = useState<string | null>(null);
    const [tempCaseExperience, setTempCaseExperience] = useState('');
    const [editingKeywords, setEditingKeywords] = useState<string | null>(null);
    const [tempKeywords, setTempKeywords] = useState('');

    // Message filter state
    const [messageFilter, setMessageFilter] = useState<'all' | 'unread' | 'urgent'>('all');
    const [messagesLoading, setMessagesLoading] = useState(false);

    const filteredMessages = allMessages.filter((message) => {
        if (messageFilter === 'all') return true;
        if (messageFilter === 'unread') return !message.read;
        if (messageFilter === 'urgent') return message.urgency === 'urgent' || message.urgency === 'emergency';
        return true;
    });

    const loadUnverifiedUsers = useCallback(async () => {
        try {
            const { data: attorneys, error: attorneyError } = await supabase
                .from('attorneys')
                .select('*')
                .eq('is_verified', false)
                .order('created_at', { ascending: false });
            if (attorneyError) console.error('Error loading attorneys:', attorneyError);

            const { data: experts, error: expertError } = await supabase
                .from('experts')
                .select('*')
                .eq('is_verified', false)
                .order('created_at', { ascending: false });
            if (expertError) console.error('Error loading experts:', expertError);

            // DEBUG: Log expert data to see if additional_docs_url exists
            console.log('🔍 Loaded experts:', experts);
            if (experts && experts.length > 0) {
                experts.forEach((exp, idx) => {
                    console.log(`🔍 Expert ${idx + 1} (${exp.full_name}):`, {
                        cv_url: exp.cv_url,
                        fee_schedule_url: exp.fee_schedule_url,
                        additional_docs_url: exp.additional_docs_url
                    });
                });
            }

            const allUsers: UnverifiedUser[] = [
                ...(attorneys || []).map(a => ({ ...a, type: 'attorney' as const })),
                ...(experts || []).map(e => ({ ...e, type: 'expert' as const }))
            ];
            allUsers.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            setUnverifiedUsers(allUsers);
        } catch (error) {
            console.error('Error loading unverified users:', error);
        }
    }, []);

    const loadVerifiedUsers = useCallback(async () => {
        try {
            const { data: attorneys, error: attorneyError } = await supabase
                .from('attorneys')
                .select('*')
                .eq('is_verified', true)
                .order('created_at', { ascending: false });
            if (attorneyError) console.error('Error loading verified attorneys:', attorneyError);

            const { data: experts, error: expertError } = await supabase
                .from('experts')
                .select('*')
                .eq('is_verified', true)
                .order('created_at', { ascending: false });
            if (expertError) console.error('Error loading verified experts:', expertError);

            const allUsers: UnverifiedUser[] = [
                ...(attorneys || []).map(a => ({ ...a, type: 'attorney' as const })),
                ...(experts || []).map(e => ({ ...e, type: 'expert' as const }))
            ];
            allUsers.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            setVerifiedUsers(allUsers);
        } catch (error) {
            console.error('Error loading verified users:', error);
        }
    }, []);

    const loadAllMessages = useCallback(async () => {
        try {
            setMessagesLoading(true);
            console.log('Loading messages...');

            const { data: messages, error } = await supabase
                .from('messages')
                .select(`
                    *,
                    attorneys!messages_attorney_id_fkey (
                        name,
                        email,
                        firm_name
                    ),
                    experts!messages_expert_id_fkey (
                        full_name,
                        email,
                        specialty
                    )
                `)
                .order('created_at', { ascending: false })
                .limit(200);

            if (error) {
                console.error('Error loading messages:', error);
                // Try fallback query without joins
                const { data: fallbackMessages, error: fallbackError } = await supabase
                    .from('messages')
                    .select('*')
                    .order('created_at', { ascending: false })
                    .limit(200);

                if (fallbackError) {
                    console.error('Fallback query also failed:', fallbackError);
                } else {
                    console.log('Loaded messages via fallback:', fallbackMessages?.length || 0);
                    setAllMessages(fallbackMessages || []);
                }
            } else {
                console.log('Successfully loaded messages:', messages?.length || 0);
                setAllMessages(messages || []);
            }
        } catch (error) {
            console.error('Error loading messages:', error);
        } finally {
            setMessagesLoading(false);
        }
    }, []);

    const loadStats = useCallback(async () => {
        try {
            const [
                { count: allAttorneysCount },
                { count: allExpertsCount },
                { count: pendingAttorneysCount },
                { count: pendingExpertsCount },
                { count: nursesCount },
                { count: messagesCount }
            ] = await Promise.all([
                supabase.from('attorneys').select('id', { count: 'exact', head: true }),
                supabase.from('experts').select('id', { count: 'exact', head: true }),
                supabase.from('attorneys').select('id', { count: 'exact', head: true }).eq('is_verified', false),
                supabase.from('experts').select('id', { count: 'exact', head: true }).eq('is_verified', false),
                supabase.from('experts').select('id', { count: 'exact', head: true }).eq('expert_type', 'nurse'),
                supabase.from('messages').select('id', { count: 'exact', head: true })
            ]);

            const totalAttorneys = allAttorneysCount || 0;
            const totalExperts = allExpertsCount || 0;
            const pendingAtty = pendingAttorneysCount || 0;
            const pendingExp = pendingExpertsCount || 0;

            setStats({
                totalUsers: totalAttorneys + totalExperts,
                pendingVerification: pendingAtty + pendingExp,
                verifiedUsers: (totalAttorneys + totalExperts) - (pendingAtty + pendingExp),
                totalMessages: messagesCount || 0,
                expertsCount: totalExperts,
                attorneysCount: totalAttorneys,
                nursesCount: nursesCount || 0
            });
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    }, []);

    const loadAllData = useCallback(async () => {
        try {
            setLoading(true);
            await Promise.all([
                loadUnverifiedUsers(),
                loadVerifiedUsers(),
                loadAllMessages(),
                loadStats()
            ]);
        } catch (error) {
            console.error('Error loading admin data:', error);
        } finally {
            setLoading(false);
        }
    }, [loadUnverifiedUsers, loadVerifiedUsers, loadAllMessages, loadStats]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    const updateCaseExperience = async (user: UnverifiedUser) => {
        if (!tempCaseExperience.trim()) return;
        setEditingCaseExperience(user.id);
        try {
            const { error } = await supabase
                .from('experts')
                .update({ case_experience: tempCaseExperience.trim() })
                .eq('id', user.id);

            if (error) throw error;

            // Update local state
            const updateUser = (u: UnverifiedUser) =>
                u.id === user.id ? { ...u, case_experience: tempCaseExperience.trim() } : u;

            setUnverifiedUsers(prev => prev.map(updateUser));
            setVerifiedUsers(prev => prev.map(updateUser));
            if (selectedUser?.id === user.id) {
                setSelectedUser({ ...selectedUser, case_experience: tempCaseExperience.trim() });
            }

            setTempCaseExperience('');
            setEditingCaseExperience(null);
            alert('Case experience updated successfully!');
        } catch (error: any) {
            console.error('Error updating case experience:', error);
            alert('Error updating case experience: ' + error.message);
        } finally {
            setEditingCaseExperience(null);
        }
    };

    const updateKeywords = async (user: UnverifiedUser) => {
        if (!tempKeywords.trim()) return;
        setEditingKeywords(user.id);
        try {
            const table = user.type === 'attorney' ? 'attorneys' : 'experts';
            const updateField = user.type === 'attorney' ? 'search_keywords' : 'extracted_keywords';

            const { error } = await supabase
                .from(table)
                .update({ [updateField]: tempKeywords.trim() })
                .eq('id', user.id);

            if (error) throw error;

            // Update local state
            const updateUser = (u: UnverifiedUser) =>
                u.id === user.id ? { ...u, [updateField]: tempKeywords.trim() } : u;

            setUnverifiedUsers(prev => prev.map(updateUser));
            setVerifiedUsers(prev => prev.map(updateUser));
            if (selectedUser?.id === user.id) {
                setSelectedUser({ ...selectedUser, [updateField]: tempKeywords.trim() });
            }

            setTempKeywords('');
            setEditingKeywords(null);
            alert('Keywords updated successfully!');
        } catch (error: any) {
            console.error('Error updating keywords:', error);
            alert('Error updating keywords: ' + error.message);
        } finally {
            setEditingKeywords(null);
        }
    };

    const markMessageAsRead = async (messageId: string) => {
        try {
            const { error } = await supabase
                .from('messages')
                .update({ read: true })
                .eq('id', messageId);

            if (error) {
                console.error('Error marking message as read:', error);
            } else {
                // Update local state
                setAllMessages(prev => prev.map(msg =>
                    msg.id === messageId ? { ...msg, read: true } : msg
                ));
            }
        } catch (error) {
            console.error('Error marking message as read:', error);
        }
    };

    const addCustomKeywords = async (user: UnverifiedUser) => {
        if (!customKeywords.trim()) return;
        setAddingKeywords(user.id);
        try {
            const table = user.type === 'attorney' ? 'attorneys' : 'experts';
            const updateField = user.type === 'attorney' ? 'search_keywords' : 'extracted_keywords';
            const existingKeywords = user.type === 'attorney'
                ? user.search_keywords || ''
                : user.extracted_keywords || '';
            const combinedKeywords = existingKeywords
                ? `${existingKeywords}, ${customKeywords.trim()}`
                : customKeywords.trim();
            const { error } = await supabase
                .from(table)
                .update({ [updateField]: combinedKeywords })
                .eq('id', user.id);
            if (error) throw error;
            setUnverifiedUsers(prev => prev.map(u =>
                u.id === user.id
                    ? { ...u, [updateField]: combinedKeywords }
                    : u
            ));
            setVerifiedUsers(prev => prev.map(u =>
                u.id === user.id
                    ? { ...u, [updateField]: combinedKeywords }
                    : u
            ));
            if (selectedUser?.id === user.id) {
                setSelectedUser({ ...selectedUser, [updateField]: combinedKeywords });
            }
            setCustomKeywords('');
            alert('Keywords added successfully!');
        } catch (error: any) {
            console.error('Error adding keywords:', error);
            alert('Error adding keywords: ' + error.message);
        } finally {
            setAddingKeywords(null);
        }
    };

    const assignTier = async (user: UnverifiedUser, tier: number) => {
        try {
            setVerifying(user.id);
            const table = user.type === 'attorney' ? 'attorneys' : 'experts';
            const { error } = await supabase
                .from(table)
                .update({ tier })
                .eq('id', user.id);
            if (error) throw error;
            setUnverifiedUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, tier } : u
            ));
            setVerifiedUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, tier } : u
            ));
            if (selectedUser?.id === user.id) {
                setSelectedUser({ ...selectedUser, tier });
            }
            alert(`${TIER_NAMES[tier as keyof typeof TIER_NAMES]} tier assigned successfully!`);
        } catch (error: any) {
            console.error('Error assigning tier:', error);
            alert('Error assigning tier: ' + error.message);
        } finally {
            setVerifying(null);
        }
    };

    const verifyUser = async (user: UnverifiedUser) => {
        try {
            setVerifying(user.id);
            const table = user.type === 'attorney' ? 'attorneys' : 'experts';
            const { error } = await supabase
                .from(table)
                .update({ is_verified: true })
                .eq('id', user.id);
            if (error) throw error;
            setUnverifiedUsers(prev => prev.filter(u => u.id !== user.id));
            setVerifiedUsers(prev => [{ ...user, is_verified: true }, ...prev]);
            setSelectedUser(null);
            await loadStats();
            alert(`${user.type === 'attorney' ? 'Legal Professional' : 'Expert'} verified successfully!`);
        } catch (error: any) {
            console.error('Error verifying user:', error);
            alert('Error verifying user: ' + error.message);
        } finally {
            setVerifying(null);
        }
    };

    const unverifyUser = async (user: UnverifiedUser) => {
        if (!window.confirm(`Are you sure you want to unverify this ${user.type === 'attorney' ? 'legal professional' : 'expert'}?`)) {
            return;
        }
        try {
            setVerifying(user.id);
            const table = user.type === 'attorney' ? 'attorneys' : 'experts';
            const { error } = await supabase
                .from(table)
                .update({ is_verified: false })
                .eq('id', user.id);
            if (error) throw error;
            setVerifiedUsers(prev => prev.filter(u => u.id !== user.id));
            setUnverifiedUsers(prev => [{ ...user, is_verified: false }, ...prev]);
            setSelectedUser(null);
            await loadStats();
            alert(`${user.type === 'attorney' ? 'Legal Professional' : 'Expert'} unverified.`);
        } catch (error: any) {
            console.error('Error unverifying user:', error);
            alert('Error unverifying user: ' + error.message);
        } finally {
            setVerifying(null);
        }
    };

    const deleteUser = async (user: UnverifiedUser) => {
        if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE this ${user.type === 'attorney' ? 'legal professional' : 'expert'}? This action cannot be undone.`)) {
            return;
        }
        const confirmText = window.prompt(`Type "DELETE" to confirm permanent deletion of ${user.type === 'attorney' ? user.name : user.full_name}:`);
        if (confirmText !== 'DELETE') {
            return;
        }
        try {
            setVerifying(user.id);
            const table = user.type === 'attorney' ? 'attorneys' : 'experts';
            const { error } = await supabase
                .from(table)
                .delete()
                .eq('id', user.id);
            if (error) throw error;
            setUnverifiedUsers(prev => prev.filter(u => u.id !== user.id));
            setVerifiedUsers(prev => prev.filter(u => u.id !== user.id));
            setSelectedUser(null);
            await loadStats();
            alert(`${user.type === 'attorney' ? 'Legal Professional' : 'Expert'} deleted permanently.`);
        } catch (error: any) {
            console.error('Error deleting user:', error);
            alert('Error deleting user: ' + error.message);
        } finally {
            setVerifying(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading admin panel...</p>
                </div>
            </div>
        );
    }

    const renderOverviewTab = () => (
        <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
                    <div className="flex items-center">
                        <svg className="h-8 w-8 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                        <div className="ml-4">
                            <p className="text-sm font-medium opacity-75">Total Users</p>
                            <p className="text-3xl font-bold">{stats.totalUsers}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg shadow p-6 text-white">
                    <div className="flex items-center">
                        <svg className="h-8 w-8 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="ml-4">
                            <p className="text-sm font-medium opacity-75">Pending Verification</p>
                            <p className="text-3xl font-bold">{stats.pendingVerification}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
                    <div className="flex items-center">
                        <svg className="h-8 w-8 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="ml-4">
                            <p className="text-sm font-medium opacity-75">Verified Users</p>
                            <p className="text-3xl font-bold">{stats.verifiedUsers}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow p-6 text-white">
                    <div className="flex items-center">
                        <svg className="h-8 w-8 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <div className="ml-4">
                            <p className="text-sm font-medium opacity-75">Total Messages</p>
                            <p className="text-3xl font-bold">{stats.totalMessages}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* User Type Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">User Type Breakdown</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Legal Professionals</span>
                            <span className="font-semibold text-blue-600">{stats.attorneysCount}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Medical Experts</span>
                            <span className="font-semibold text-green-600">{stats.expertsCount}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Nurses</span>
                            <span className="font-semibold text-purple-600">{stats.nursesCount}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                    <div className="space-y-3">
                        {unverifiedUsers.slice(0, 3).map(user => (
                            <div key={user.id} className="text-sm">
                                <p className="font-medium">{user.type === 'attorney' ? user.name : user.full_name}</p>
                                <p className="text-gray-500">New {user.type === 'attorney' ? 'legal professional' : user.type} registration</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                        <button
                            onClick={() => setActiveTab('pending')}
                            className="w-full text-left px-3 py-2 text-sm bg-yellow-50 text-yellow-800 rounded hover:bg-yellow-100"
                        >
                            Review Pending ({stats.pendingVerification})
                        </button>
                        <button
                            onClick={() => setActiveTab('messages')}
                            className="w-full text-left px-3 py-2 text-sm bg-purple-50 text-purple-800 rounded hover:bg-purple-100"
                        >
                            View All Messages ({stats.totalMessages})
                        </button>
                        <button
                            onClick={() => setActiveTab('verified')}
                            className="w-full text-left px-3 py-2 text-sm bg-green-50 text-green-800 rounded hover:bg-green-100"
                        >
                            Manage Verified ({stats.verifiedUsers})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderUserList = (users: UnverifiedUser[]) => (
        <div className="space-y-4">
            {users.length === 0 ? (
                <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="mt-2 text-gray-500">No users found</p>
                </div>
            ) : (
                users.map((user) => (
                    <div
                        key={user.id}
                        data-testid="user-card"
                        className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer bg-white"
                        onClick={() => setSelectedUser(user)}
                    >
                        <div className="flex justify-between items-start">
                            <div className="flex items-start space-x-4">
                                {/* Profile picture with tier border */}
                                <div className={`w-16 h-16 rounded-full overflow-hidden border-4 ${
                                    user.tier ? TIER_COLORS[user.tier as keyof typeof TIER_COLORS].border : 'border-gray-300'
                                }`}>
                                    <img
                                        src={user.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.type === 'attorney' ? user.name || '' : user.full_name || '')}&background=0D8ABC&color=fff&size=200`}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                        <h4 className="font-semibold text-gray-900">
                                            {user.type === 'attorney' ? user.name : user.full_name}
                                        </h4>
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                            user.type === 'attorney'
                                                ? 'bg-blue-100 text-blue-800'
                                                : user.expert_type === 'nurse'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : 'bg-green-100 text-green-800'
                                        }`}>
                                            {user.type === 'attorney' ? 'Legal Professional' : user.expert_type === 'nurse' ? 'Nurse' : 'Expert'}
                                        </span>
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                            user.is_verified
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {user.is_verified ? 'Verified' : 'Pending'}
                                        </span>
                                        {user.tier && (
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                user.tier === 1 ? 'bg-yellow-100 text-yellow-800' :
                                                    user.tier === 2 ? 'bg-purple-100 text-purple-800' :
                                                        user.tier === 3 ? 'bg-blue-100 text-blue-800' :
                                                            user.tier === 4 ? 'bg-green-100 text-green-800' :
                                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {TIER_NAMES[user.tier as keyof typeof TIER_NAMES]}
                                            </span>
                                        )}
                                        {(user.search_keywords || user.extracted_keywords) && (
                                            <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                                                Keywords Added
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {user.type === 'attorney'
                                            ? `${user.firm_name} • ${user.state_of_practice} • ${user.years_experience || 0} years`
                                            : `${user.specialty} • ${user.years_practicing} years${user.expert_type === 'nurse' ? ` • ${user.nursing_degree || 'RN'}` : ''}`
                                        }
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        Email: {user.email}
                                    </p>
                                    {user.type === 'expert' && user.case_experience && (
                                        <p className="text-sm text-gray-500 mt-1 italic line-clamp-1">
                                            Case: {user.case_experience}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-500">
                                    {new Date(user.created_at).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Expert Link Pro Admin Panel</h1>
                            <p className="text-gray-600 mt-2">Complete system administration and user management</p>
                        </div>
                        <div className="text-right">
                            <div className="px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                                ✅ Secure Admin Access
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-lg shadow">
                    <div className="border-b">
                        <nav className="flex -mb-px">
                            {[
                                { id: 'overview', label: 'Overview', icon: '📊' },
                                { id: 'pending', label: `Pending (${stats.pendingVerification})`, icon: '⏳' },
                                { id: 'verified', label: `Verified (${stats.verifiedUsers})`, icon: '✅' },
                                { id: 'messages', label: `Messages (${stats.totalMessages})`, icon: '💬' }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as any)}
                                    className={`py-4 px-6 text-sm font-medium border-b-2 transition flex items-center space-x-2 ${
                                        activeTab === tab.id
                                            ? 'border-purple-600 text-purple-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <span>{tab.icon}</span>
                                    <span>{tab.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="p-6">
                        {activeTab === 'overview' && renderOverviewTab()}

                        {activeTab === 'pending' && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                                    Users Pending Verification ({unverifiedUsers.length})
                                </h3>
                                {renderUserList(unverifiedUsers)}
                            </div>
                        )}

                        {activeTab === 'verified' && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                                    Verified Users ({verifiedUsers.length})
                                </h3>
                                {renderUserList(verifiedUsers)}
                            </div>
                        )}

                        {activeTab === 'messages' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        All Platform Messages ({allMessages.length})
                                    </h3>
                                    <button
                                        onClick={loadAllMessages}
                                        disabled={messagesLoading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                                    >
                                        {messagesLoading ? 'Refreshing...' : 'Refresh Messages'}
                                    </button>
                                </div>

                                {/* Filter buttons */}
                                <div className="flex space-x-4 mb-6">
                                    <button
                                        onClick={() => setMessageFilter('all')}
                                        className={`px-4 py-2 rounded ${messageFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                    >
                                        All Messages ({allMessages.length})
                                    </button>
                                    <button
                                        onClick={() => setMessageFilter('unread')}
                                        className={`px-4 py-2 rounded ${messageFilter === 'unread' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                    >
                                        Unread Only ({allMessages.filter(m => !m.read).length})
                                    </button>
                                    <button
                                        onClick={() => setMessageFilter('urgent')}
                                        className={`px-4 py-2 rounded ${messageFilter === 'urgent' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                    >
                                        Urgent/Emergency ({allMessages.filter(m => m.urgency === 'urgent' || m.urgency === 'emergency').length})
                                    </button>
                                </div>

                                {messagesLoading ? (
                                    <div className="text-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                                        <p className="mt-2 text-gray-500">Loading messages...</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {filteredMessages.length === 0 ? (
                                            <div className="text-center py-12">
                                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                </svg>
                                                <p className="mt-2 text-gray-500">
                                                    {messageFilter === 'all' ? 'No messages found' :
                                                        messageFilter === 'unread' ? 'No unread messages' :
                                                            'No urgent messages'}
                                                </p>
                                                {allMessages.length > 0 && messageFilter !== 'all' && (
                                                    <button
                                                        onClick={() => setMessageFilter('all')}
                                                        className="mt-2 text-blue-600 hover:text-blue-800"
                                                    >
                                                        View all messages
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            filteredMessages.map((message) => (
                                                <div
                                                    key={message.id}
                                                    className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer bg-white ${
                                                        !message.read ? 'border-l-4 border-l-blue-500' : ''
                                                    }`}
                                                    onClick={() => {
                                                        setSelectedMessage(message);
                                                        if (!message.read) {
                                                            markMessageAsRead(message.id);
                                                        }
                                                    }}
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            <div className="flex items-center space-x-2">
                                                                <h4 className="font-semibold text-gray-900">
                                                                    {message.case_title || 'Case Communication'}
                                                                </h4>
                                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                                    message.sender_type === 'attorney'
                                                                        ? 'bg-blue-100 text-blue-800'
                                                                        : 'bg-green-100 text-green-800'
                                                                }`}>
                                                                    From {message.sender_type === 'attorney' ? 'Attorney' : 'Expert'}
                                                                </span>
                                                                {message.urgency && (
                                                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                                                        message.urgency === 'emergency' ? 'bg-red-100 text-red-800' :
                                                                            message.urgency === 'urgent' ? 'bg-orange-100 text-orange-800' :
                                                                                'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                        {message.urgency}
                                                                    </span>
                                                                )}
                                                                {!message.read && (
                                                                    <span className="px-2 py-1 text-xs rounded-full bg-blue-600 text-white">
                                                                        Unread
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-gray-600 mt-1">
                                                                {message.sender_type === 'attorney'
                                                                    ? `${message.attorneys?.name || 'Attorney'} (${message.attorneys?.firm_name || 'Law Firm'}) → ${message.experts?.full_name || 'Expert'}`
                                                                    : `${message.experts?.full_name || 'Expert'} → ${message.attorneys?.name || 'Attorney'} (${message.attorneys?.firm_name || 'Law Firm'})`
                                                                }
                                                            </p>
                                                            <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                                                                {message.message.length > 150
                                                                    ? message.message.substring(0, 150) + '...'
                                                                    : message.message
                                                                }
                                                            </p>
                                                        </div>
                                                        <div className="text-right ml-4">
                                                            <p className="text-xs text-gray-500">
                                                                {new Date(message.created_at).toLocaleDateString()}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {new Date(message.created_at).toLocaleTimeString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Enhanced User Detail Modal with Tier Assignment */}
                {selectedUser && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">
                                            {selectedUser.type === 'attorney' ? 'Legal Professional' : selectedUser.expert_type === 'nurse' ? 'Nurse' : 'Expert'} Details
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            {selectedUser.type === 'attorney' ? selectedUser.name : selectedUser.full_name}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setSelectedUser(null)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Tier Assignment Section */}
                                <div className="mb-6 p-4 bg-purple-50 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-3">Tier Assignment</h4>
                                    <div className="grid grid-cols-5 gap-2 mb-3">
                                        {Object.entries(TIER_NAMES).map(([tier, name]) => (
                                            <button
                                                key={tier}
                                                onClick={() => setSelectedTier(Number(tier))}
                                                className={`p-2 text-xs rounded border-2 ${
                                                    selectedTier === Number(tier)
                                                        ? TIER_COLORS[Number(tier) as keyof typeof TIER_COLORS].border + ' bg-opacity-20'
                                                        : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                            >
                                                <div className={`w-6 h-6 rounded-full mx-auto mb-1 border-2 ${
                                                    TIER_COLORS[Number(tier) as keyof typeof TIER_COLORS].border
                                                }`}></div>
                                                <div className="font-medium">{name}</div>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => assignTier(selectedUser, selectedTier)}
                                            disabled={verifying === selectedUser.id}
                                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
                                        >
                                            {verifying === selectedUser.id ? 'Assigning...' : `Assign ${TIER_NAMES[selectedTier as keyof typeof TIER_NAMES]} Tier`}
                                        </button>
                                        {selectedUser.tier && (
                                            <span className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded">
                                                Current: {TIER_NAMES[selectedUser.tier as keyof typeof TIER_NAMES]}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* User Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    {selectedUser.type === 'attorney' ? (
                                        <>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-3">Legal Professional Information</h4>
                                                <div className="space-y-2 text-sm">
                                                    <p><strong>Name:</strong> {selectedUser.name}</p>
                                                    <p><strong>Firm:</strong> {selectedUser.firm_name}</p>
                                                    <p><strong>License:</strong> {selectedUser.license_number || 'Not provided'}</p>
                                                    <p><strong>State:</strong> {selectedUser.state_of_practice}</p>
                                                    <p><strong>Phone:</strong> {selectedUser.phone_number}</p>
                                                    <p><strong>Email:</strong> {selectedUser.email}</p>
                                                    <p><strong>Experience:</strong> {selectedUser.years_experience || 0} years</p>
                                                    <p><strong>Firm Size:</strong> {selectedUser.firm_size || 'Not specified'}</p>
                                                    <p><strong>User ID:</strong> <code className="text-xs bg-gray-100 px-1 rounded">{selectedUser.user_id}</code></p>
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-3">Practice Details</h4>
                                                <div className="space-y-2 text-sm">
                                                    <p><strong>Practice Areas:</strong> {selectedUser.practice_areas?.join(', ') || 'Not specified'}</p>
                                                    <p><strong>Search Keywords:</strong> {selectedUser.search_keywords || 'None provided'}</p>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-3">Expert Information</h4>
                                                <div className="space-y-2 text-sm">
                                                    <p><strong>Name:</strong> {selectedUser.full_name}</p>
                                                    <p><strong>Type:</strong> {selectedUser.expert_type || 'Physician'}</p>
                                                    <p><strong>NPI/License:</strong> {selectedUser.npi || 'Not provided'}</p>
                                                    <p><strong>Title:</strong> {selectedUser.job_title}</p>
                                                    <p><strong>Specialty:</strong> {selectedUser.specialty}</p>
                                                    <p><strong>Sub-specialty:</strong> {selectedUser.sub_specialty || 'None'}</p>
                                                    <p><strong>Years:</strong> {selectedUser.years_practicing}</p>
                                                    <p><strong>Phone:</strong> {selectedUser.phone_number}</p>
                                                    <p><strong>Email:</strong> {selectedUser.email}</p>
                                                    <p><strong>User ID:</strong> <code className="text-xs bg-gray-100 px-1 rounded">{selectedUser.user_id}</code></p>
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-3">Professional Details</h4>
                                                <div className="space-y-2 text-sm">
                                                    <p><strong>States Licensed:</strong> {selectedUser.states_licensed?.join(', ') || 'Not provided'}</p>
                                                    <p><strong>Certifications:</strong> {selectedUser.certifications?.join(', ') || 'Not provided'}</p>
                                                    {selectedUser.expert_type === 'nurse' && (
                                                        <>
                                                            <p><strong>Nursing Degree:</strong> {selectedUser.nursing_degree || 'Not specified'}</p>
                                                            <p><strong>Years as RN:</strong> {selectedUser.years_as_nurse || 'Not specified'}</p>
                                                            <p><strong>Nursing Certs:</strong> {selectedUser.nursing_certifications?.join(', ') || 'None'}</p>
                                                        </>
                                                    )}
                                                    <p><strong>Retainer Fee:</strong> ${selectedUser.retainer_fee?.toLocaleString()}</p>
                                                    <p><strong>Review Rate:</strong> ${selectedUser.rate_review}/hr</p>
                                                    <p><strong>Deposition Rate:</strong> ${selectedUser.rate_deposition}/hr</p>
                                                    <p><strong>Trial Rate:</strong> ${selectedUser.rate_trial}/hr</p>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>


                                {/* CRITICAL INFORMATION FOR ADMIN REVIEW */}
                                {selectedUser.type === 'expert' && (
                                    <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                                        <h4 className="font-bold text-yellow-800 mb-4 flex items-center">
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            Critical Information - Review Carefully
                                        </h4>

                                        {/* License Actions */}
                                        <div className="mb-4 p-3 bg-white rounded border">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium text-gray-900">Prior License Actions:</span>
                                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                                    selectedUser.prior_license_actions
                                                        ? 'bg-red-100 text-red-800'
                                                        : 'bg-green-100 text-green-800'
                                                }`}>
                                                    {selectedUser.prior_license_actions ? '⚠️ YES' : '✓ NO'}
                                                </span>
                                            </div>
                                            {selectedUser.prior_license_actions && selectedUser.license_action_details && (
                                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                                                    <strong>Details:</strong> {selectedUser.license_action_details}
                                                </div>
                                            )}
                                        </div>

                                        {/* Current Lawsuit */}
                                        <div className="mb-4 p-3 bg-white rounded border">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium text-gray-900">Currently Named in Lawsuit:</span>
                                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                                    selectedUser.current_lawsuit
                                                        ? 'bg-red-100 text-red-800'
                                                        : 'bg-green-100 text-green-800'
                                                }`}>
                                                    {selectedUser.current_lawsuit ? '⚠️ YES' : '✓ NO'}
                                                </span>
                                            </div>
                                            {selectedUser.current_lawsuit && selectedUser.lawsuit_details && (
                                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                                                    <strong>Details:</strong> {selectedUser.lawsuit_details}
                                                </div>
                                            )}
                                        </div>

                                        {/* Practice Status */}
                                        <div className="p-3 bg-white rounded border">
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium text-gray-900">Currently Practicing:</span>
                                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                                    selectedUser.currently_practicing
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {selectedUser.currently_practicing ? '✓ YES' : 'NO'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Expert Statistics & Experience */}
                                {selectedUser.type === 'expert' && (
                                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                                        <h4 className="font-semibold text-blue-900 mb-4">Experience & Statistics</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-2xl font-bold text-blue-600">{selectedUser.num_defense_depositions || 0}</div>
                                                <div className="text-xs text-gray-600">Defense Depositions</div>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-2xl font-bold text-blue-600">{selectedUser.num_plaintiff_depositions || 0}</div>
                                                <div className="text-xs text-gray-600">Plaintiff Depositions</div>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-2xl font-bold text-purple-600">{selectedUser.num_defense_trials || 0}</div>
                                                <div className="text-xs text-gray-600">Defense Trials</div>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-2xl font-bold text-purple-600">{selectedUser.num_plaintiff_trials || 0}</div>
                                                <div className="text-xs text-gray-600">Plaintiff Trials</div>
                                            </div>
                                        </div>
                                        <div className="mt-4 grid grid-cols-2 gap-4">
                                            <div className="bg-white p-3 rounded border">
                                                <span className="text-sm text-gray-600">Years Practicing:</span>
                                                <span className="ml-2 font-bold">{selectedUser.years_practicing || 'N/A'}</span>
                                            </div>
                                            <div className="bg-white p-3 rounded border">
                                                <span className="text-sm text-gray-600">Published Scholarly Work:</span>
                                                <span className={`ml-2 font-bold ${selectedUser.published_scholarly ? 'text-green-600' : 'text-gray-500'}`}>
                                                    {selectedUser.published_scholarly ? 'Yes' : 'No'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Daubert History & Case Outcomes */}
                                {selectedUser.type === 'expert' && (
                                    <div className="mb-6 p-4 bg-orange-50 rounded-lg">
                                        <h4 className="font-semibold text-orange-900 mb-4">Daubert/Frye & Case Outcomes</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-sm font-medium text-gray-600 mb-1">Daubert History</div>
                                                <div className={`text-sm font-bold ${
                                                    selectedUser.daubert_challenge_history === 'never_challenged' ? 'text-green-600' :
                                                        selectedUser.daubert_challenge_history === 'challenged_admitted' ? 'text-blue-600' :
                                                            selectedUser.daubert_challenge_history === 'challenged_excluded' ? 'text-red-600' :
                                                                'text-orange-600'
                                                }`}>
                                                    {selectedUser.daubert_challenge_history === 'never_challenged' ? 'Never Challenged' :
                                                        selectedUser.daubert_challenge_history === 'challenged_admitted' ? 'Challenged — Admitted' :
                                                            selectedUser.daubert_challenge_history === 'challenged_excluded' ? 'Challenged — Excluded' :
                                                                selectedUser.daubert_challenge_history === 'challenged_mixed' ? 'Mixed Results' :
                                                                    selectedUser.daubert_challenge_history || 'Not Provided'}
                                                </div>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-sm font-medium text-gray-600 mb-1">Remote Testimony</div>
                                                <div className={`text-sm font-bold ${selectedUser.remote_testimony ? 'text-green-600' : 'text-red-600'}`}>
                                                    {selectedUser.remote_testimony ? 'Available' : 'In-Person Only'}
                                                </div>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-sm font-medium text-gray-600 mb-1">Avg Response Time</div>
                                                <div className="text-sm font-bold text-blue-600">
                                                    {selectedUser.avg_response_time_hours ? `${selectedUser.avg_response_time_hours}h` : 'No data'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-2xl font-bold text-green-600">{selectedUser.cases_won || 0}</div>
                                                <div className="text-xs text-gray-600">Cases Won</div>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-2xl font-bold text-red-600">{selectedUser.cases_lost || 0}</div>
                                                <div className="text-xs text-gray-600">Cases Lost</div>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-2xl font-bold text-yellow-600">{selectedUser.cases_settled || 0}</div>
                                                <div className="text-xs text-gray-600">Cases Settled</div>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                                <div className="text-2xl font-bold text-blue-600">{selectedUser.cases_pending || 0}</div>
                                                <div className="text-xs text-gray-600">Cases Pending</div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-white p-3 rounded border">
                                                <span className="text-sm text-gray-600">Languages:</span>
                                                <span className="ml-2 font-medium">{selectedUser.languages?.join(', ') || 'English'}</span>
                                            </div>
                                            <div className="bg-white p-3 rounded border">
                                                <span className="text-sm text-gray-600">Last Active:</span>
                                                <span className="ml-2 font-medium">{selectedUser.last_active_at ? new Date(selectedUser.last_active_at).toLocaleString() : 'Never'}</span>
                                            </div>
                                            {selectedUser.referral_sources && (
                                                <div className="bg-white p-3 rounded border md:col-span-2">
                                                    <span className="text-sm text-gray-600">Referral Source:</span>
                                                    <span className="ml-2 font-medium">{selectedUser.referral_sources}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Enhanced Keyword & Case Experience Management */}
                                <div className="mb-6 space-y-6">
                                    {/* Case Experience Edit (for experts only) */}
                                    {selectedUser.type === 'expert' && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-3">Case Experience Management</h4>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Current Case Experience:
                                                    </label>
                                                    <div className="bg-gray-50 p-3 rounded text-sm text-gray-600 mb-2">
                                                        {selectedUser.case_experience || 'No case experience provided'}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Edit Case Experience:
                                                    </label>
                                                    <div className="space-y-2">
                                                        <textarea
                                                            value={tempCaseExperience || selectedUser.case_experience || ''}
                                                            onChange={(e) => setTempCaseExperience(e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                                                            rows={4}
                                                            placeholder="e.g., Testified in 15+ medical malpractice cases, specializing in cardiac surgery complications..."
                                                        />
                                                        <button
                                                            onClick={() => {
                                                                if (!tempCaseExperience) {
                                                                    setTempCaseExperience(selectedUser.case_experience || '');
                                                                } else {
                                                                    updateCaseExperience(selectedUser);
                                                                }
                                                            }}
                                                            disabled={editingCaseExperience === selectedUser.id}
                                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                                        >
                                                            {editingCaseExperience === selectedUser.id ? 'Updating...' :
                                                                !tempCaseExperience ? 'Load Current' : 'Update Case Experience'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Keyword Management */}
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-3">Keyword Management</h4>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Current Keywords:
                                                </label>
                                                <div className="bg-gray-50 p-3 rounded text-sm text-gray-600 mb-2">
                                                    {selectedUser.type === 'attorney'
                                                        ? selectedUser.search_keywords || 'No keywords'
                                                        : selectedUser.extracted_keywords || 'No keywords'
                                                    }
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Edit Keywords (Replace All):
                                                </label>
                                                <div className="space-y-2">
                                                    <textarea
                                                        value={tempKeywords || (selectedUser.type === 'attorney' ? selectedUser.search_keywords : selectedUser.extracted_keywords) || ''}
                                                        onChange={(e) => setTempKeywords(e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                                                        rows={3}
                                                        placeholder="e.g., cardiac surgery, expert testimony, board certified, medical malpractice"
                                                    />
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => {
                                                                if (!tempKeywords) {
                                                                    setTempKeywords(selectedUser.type === 'attorney'
                                                                        ? selectedUser.search_keywords || ''
                                                                        : selectedUser.extracted_keywords || '');
                                                                } else {
                                                                    updateKeywords(selectedUser);
                                                                }
                                                            }}
                                                            disabled={editingKeywords === selectedUser.id}
                                                            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400"
                                                        >
                                                            {editingKeywords === selectedUser.id ? 'Updating...' :
                                                                !tempKeywords ? 'Load Current' : 'Replace All Keywords'}
                                                        </button>

                                                        <button
                                                            onClick={() => {
                                                                setTempKeywords('');
                                                                setTempCaseExperience('');
                                                            }}
                                                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                                        >
                                                            Clear
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Add to existing keywords section */}
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Add to Existing Keywords:
                                                </label>
                                                <div className="flex space-x-2">
                                                    <input
                                                        type="text"
                                                        value={customKeywords}
                                                        onChange={(e) => setCustomKeywords(e.target.value)}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                                                        placeholder="Add additional keywords..."
                                                    />
                                                    <button
                                                        onClick={() => addCustomKeywords(selectedUser)}
                                                        disabled={addingKeywords === selectedUser.id || !customKeywords.trim()}
                                                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                                    >
                                                        {addingKeywords === selectedUser.id ? 'Adding...' : 'Add Keywords'}
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    This will append to existing keywords without removing them
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Documents Section - Add this after Keyword Management */}
                                    {selectedUser.type === 'expert' && (
                                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                            <h4 className="font-semibold text-gray-900 mb-3">Documents & Files</h4>
                                            <div className="space-y-3">
                                                {/* CV */}
                                                {selectedUser.cv_url && (
                                                    <div className="flex items-center justify-between p-3 bg-white rounded border">
                                                        <div>
                                                            <p className="font-medium text-gray-900">Curriculum Vitae</p>
                                                            <p className="text-xs text-gray-500">CV Document</p>
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <a
                                                                href={selectedUser.cv_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                                                            >
                                                                View CV
                                                            </a>
                                                            <button
                                                                onClick={() => {
                                                                    const link = document.createElement('a');
                                                                    link.href = selectedUser.cv_url!;
                                                                    link.download = `${selectedUser.full_name}-CV.pdf`;
                                                                    link.click();
                                                                }}
                                                                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                                            >
                                                                Download
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Fee Schedule */}
                                                {selectedUser.fee_schedule_url && (
                                                    <div className="flex items-center justify-between p-3 bg-white rounded border">
                                                        <div>
                                                            <p className="font-medium text-gray-900">Fee Schedule</p>
                                                            <p className="text-xs text-gray-500">Pricing Document</p>
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <a
                                                                href={selectedUser.fee_schedule_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                                                            >
                                                                View Schedule
                                                            </a>
                                                            <button
                                                                onClick={() => {
                                                                    const link = document.createElement('a');
                                                                    link.href = selectedUser.fee_schedule_url!;
                                                                    link.download = `${selectedUser.full_name}-FeeSchedule.pdf`;
                                                                    link.click();
                                                                }}
                                                                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                                            >
                                                                Download
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Additional Documents */}
                                                {selectedUser.additional_docs_url && (
                                                    <div className="p-3 bg-white rounded border">
                                                        <p className="font-medium text-gray-900 mb-2">Additional Documents</p>
                                                        {(() => {
                                                            // Check if it's a JSON array of URLs or a single URL
                                                            let urls: string[] = [];
                                                            try {
                                                                const parsed = JSON.parse(selectedUser.additional_docs_url);
                                                                urls = Array.isArray(parsed) ? parsed : [selectedUser.additional_docs_url];
                                                            } catch {
                                                                urls = [selectedUser.additional_docs_url];
                                                            }
                                                            return (
                                                                <div className="space-y-2">
                                                                    {urls.map((url, idx) => (
                                                                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                                                            <span className="text-sm text-gray-700">Document {idx + 1}</span>
                                                                            <div className="flex space-x-2">
                                                                                <a
                                                                                    href={url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                                                                >
                                                                                    View
                                                                                </a>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        const link = document.createElement('a');
                                                                                        link.href = url;
                                                                                        link.download = `${selectedUser.full_name}-AdditionalDoc-${idx + 1}.pdf`;
                                                                                        link.click();
                                                                                    }}
                                                                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                                                                >
                                                                                    Download
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}

                                                {!selectedUser.cv_url && !selectedUser.fee_schedule_url && !selectedUser.additional_docs_url && (
                                                    <p className="text-sm text-gray-500 italic">No documents uploaded</p>
                                                )}
                                            </div>
                                        </div>
                                    )}


                                </div>



                                {/* Action Buttons */}
                                <div className="flex space-x-4">
                                    {!selectedUser.is_verified ? (
                                        <button
                                            onClick={() => verifyUser(selectedUser)}
                                            disabled={verifying === selectedUser.id}
                                            className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                                        >
                                            {verifying === selectedUser.id ? 'Verifying...' : 'Verify & Approve'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => unverifyUser(selectedUser)}
                                            disabled={verifying === selectedUser.id}
                                            className="flex-1 py-2 px-4 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-400"
                                        >
                                            {verifying === selectedUser.id ? 'Processing...' : 'Remove Verification'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => deleteUser(selectedUser)}
                                        disabled={verifying === selectedUser.id}
                                        className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                                    >
                                        {verifying === selectedUser.id ? 'Processing...' : 'Delete User'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Enhanced Message Detail Modal */}
                {selectedMessage && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">
                                            Message Details
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            {selectedMessage.case_title || 'Case Communication'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setSelectedMessage(null)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="text-gray-600">From:</span>
                                                <p className="font-medium">
                                                    {selectedMessage.sender_type === 'attorney'
                                                        ? selectedMessage.attorneys?.name || 'Legal Professional'
                                                        : selectedMessage.experts?.full_name || 'Expert'
                                                    }
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {selectedMessage.attorneys?.email || selectedMessage.experts?.email}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">To:</span>
                                                <p className="font-medium">
                                                    {selectedMessage.sender_type === 'expert'
                                                        ? selectedMessage.attorneys?.name || 'Legal Professional'
                                                        : selectedMessage.experts?.full_name || 'Expert'
                                                    }
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Date:</span>
                                                <p className="font-medium">{new Date(selectedMessage.created_at).toLocaleString()}</p>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Status:</span>
                                                <p className="font-medium">{selectedMessage.read ? 'Read' : 'Unread'}</p>
                                            </div>
                                            {selectedMessage.urgency && (
                                                <div>
                                                    <span className="text-gray-600">Urgency:</span>
                                                    <p className="font-medium capitalize">{selectedMessage.urgency}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Message Content</h4>
                                        <div className="bg-white border border-gray-200 rounded p-4 max-h-96 overflow-y-auto">
                                            <p className="whitespace-pre-wrap">{selectedMessage.message}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}